import React from 'react';
import { css } from '@emotion/react';
import { i18n_2700_articles_podcasts_and_resources } from '@yi/i18n/phrases/i18n_2700_articles_podcasts_and_resources';
import { i18n_3000_on_demand_yoga_practices } from '@yi/i18n/phrases/i18n_3000_on_demand_yoga_practices';
import { i18n_300_courses_and_challenges } from '@yi/i18n/phrases/i18n_300_courses_and_challenges';
import { i18n_700_audio_and_video_meditations } from '@yi/i18n/phrases/i18n_700_audio_and_video_meditations';
import { i18n_discount_on_select_trainings__v2 } from '@yi/i18n/phrases/i18n_discount_on_select_trainings__v2';
import { i18n_monthly_membership } from '@yi/i18n/phrases/i18n_monthly_membership';
import { i18n_per_month__v2 } from '@yi/i18n/phrases/i18n_per_month__v2';
import { i18n_unlimited_access_to } from '@yi/i18n/phrases/i18n_unlimited_access_to';

import { useMonthlyPrice } from '~/src/common/membership';
import { PREMIUM_COURSES_MONTHLY_DISCOUNT } from '~/src/common/membership/constants';
import { useMonthlySignUpUrl } from '~/src/common/sign-up';
import { ErrorMessage } from '~/src/components/ErrorMessage';
import { useIsSSR } from '~/src/components/hooks/useIsSSR';
import { useLocale } from '~/src/components/LocaleProvider';
import { paddingVariables } from '~/src/styles/design-system';
import { breakpoints } from '~/src/styles/styled-mixins';

import { Benefit } from './Benefit';
import { commonStyles } from './common-styles';
import { ReactComponent as Mandala } from './mandala.svg';
import { PricePlaceholder } from './PricePlaceholder';
import { StartButton } from './StartButton';

const styles = {
  container: css({
    [breakpoints.minDesktop]: {
      gridTemplateAreas: `'. .' '. .' 'title benefits' '. benefits' 'price benefits' '. benefits' 'button benefits'  '. .' '. .'`,
      gridTemplateColumns: '1fr 1fr',
      gridTemplateRows: `${paddingVariables.p120} ${paddingVariables.p120} auto ${paddingVariables.p24} auto ${paddingVariables.p24} auto ${paddingVariables.p120} ${paddingVariables.p120}`,
      width: '100%',
    },
  }),
  mandala: css({
    gridColumn: '1/2',
  }),
  benefits: css({
    [breakpoints.minDesktop]: {
      justifySelf: 'end',
    },
  }),
};

export const MonthlyPlan: React.FC<{ className?: string }> = props => {
  const { locale } = useLocale();

  const { error, loading, price } = useMonthlyPrice();

  const { href } = useMonthlySignUpUrl();

  const isSSR = useIsSSR();
  const errorMsg = isSSR ? null : error;

  return (
    <section css={[commonStyles.container, styles.container]} {...props}>
      <Mandala css={[commonStyles.mandala, styles.mandala]} />
      <h1 css={commonStyles.title}>{i18n_monthly_membership(locale)}</h1>
      <div css={[commonStyles.benefits, styles.benefits]}>
        <h2 css={commonStyles.benefitsTitle}>{i18n_unlimited_access_to(locale)}:</h2>
        <Benefit>{i18n_3000_on_demand_yoga_practices(locale)}</Benefit>
        <Benefit>{i18n_700_audio_and_video_meditations(locale)}</Benefit>
        <Benefit>{i18n_2700_articles_podcasts_and_resources(locale)}</Benefit>
        <Benefit>{i18n_300_courses_and_challenges(locale)}</Benefit>
        <Benefit>
          <b>{PREMIUM_COURSES_MONTHLY_DISCOUNT}</b> {i18n_discount_on_select_trainings__v2(locale)}
        </Benefit>
      </div>
      <div css={commonStyles.price}>
        {errorMsg ? (
          <ErrorMessage message={errorMsg} css={commonStyles.errorMsg} />
        ) : (
          <span>
            {loading ? (
              <PricePlaceholder uniqueKey="monthly-price-placeholder" widthRatio={4} />
            ) : (
              price
            )}
            {` ${i18n_per_month__v2(locale)}`}
          </span>
        )}
      </div>
      <StartButton href={href} loading={loading} css={commonStyles.button} />
    </section>
  );
};
